'use client';

import { SDKResponse } from '@commercetools/frontend-sdk';
import Cookies from 'universal-cookie';
import { WithSDKError } from 'shared/types/shared';
import { authenticateUser } from 'app/actions';
import { getEnvOrFail } from 'env/env';
import { rootStore, setAccountId, setIsSessionInvalid } from 'store';
import { isCookieValid } from './is-cookie-valid';

const waitForSessionCookie = async (
  customerId: string,
  name = 'frontastic-session',
  delay = 100,
  max_attempts = 6,
) => {
  let attempts = 0;
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const cookieValue = new Cookies().get<string>(name);
    if (cookieValue) {
      return;
    }
    attempts++;
    if (attempts > max_attempts) {
      break;
    }
    console.info(
      `Waiting for cookie '${name}' to be set. Attempt ${attempts} of ${max_attempts} for customer: ${customerId}`,
    );
    // eslint-disable-next-line no-await-in-loop
    await new Promise((resolve) => {
      setTimeout(resolve, delay);
    });
  }

  console.warn(
    `Cookie '${name}' was not set after all retry attempts for customer: ${customerId}`,
  );
};

export const withCookieValidation = async <T extends WithSDKError>(
  fn: () => Promise<SDKResponse<T>>,
  ensureSessionCookieExists = false,
  customerId = 'anonymous',
): Promise<SDKResponse<T>> => {
  const locale = 'en';

  if (ensureSessionCookieExists) {
    await waitForSessionCookie(customerId);
  }

  if (!isCookieValid(30 * 60)) {
    try {
      const loginRefreshEndpoint = getEnvOrFail(
        'NEXT_PUBLIC_EDX_LOGIN_REFRESH_ENDPOINT',
      );
      const loginRefreshResponse = await fetch(loginRefreshEndpoint, {
        method: 'POST',
        credentials: 'include',
      });

      if (!loginRefreshResponse.ok) {
        throw new Error(
          `Refresh failed with response status: ${loginRefreshResponse.statusText}`,
        );
      }
    } catch (error) {
      console.error('Error during login refresh:', error);
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      rootStore.dispatch(setIsSessionInvalid(true));
      return {
        data: {
          isError: true,
          error: {
            message: 'failed to refresh user session',
            code: 'authentication_error',
            status: 401,
          },
        },
      } as SDKResponse<T>;
    }

    console.info(
      `Re-authenticating customer: ${customerId} after cookie validation failure`,
    );

    const accountResult = await authenticateUser(locale);

    if (
      !accountResult ||
      !!accountResult.isError ||
      !!accountResult.data.isError ||
      !accountResult.data.loggedIn
    ) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-call
      rootStore.dispatch(setIsSessionInvalid(true));
      return {
        data: {
          isError: true,
          error: {
            message: 'SSO error',
            code: 'authentication_error',
            status: 401,
          },
        },
      } as SDKResponse<T>;
    }
    if (accountResult.data?.account?.accountId) {
      rootStore.dispatch(setAccountId(accountResult.data.account.accountId));
    }
  }

  const response = await fn();

  if (
    !response.isError &&
    response.data.isError &&
    (response.data.error.code === 'account_authentication_error' ||
      response.data.error.message.includes('jwt expired'))
  ) {
    rootStore.dispatch(setIsSessionInvalid(true));
  }

  return response;
};
